import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import React, {
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { clearLocalStorage, useLocalStorage } from 'src/hooks/useLocalStorage';
import { type LoginResult, useLogin } from 'src/hooks/useLogin';
import { resetAmplitude } from 'src/lib/analytics/amplitude/helpers/resetAmplitude';
import { BackendRouter } from 'src/lib/routers';

import type { UserIdentity } from '../hooks/useIdentity';
import { useIdentity } from '../hooks/useIdentity';
import type { AccountIdentityAccountFragment } from '../lib/graphqlTypes/types';

interface AuthContextType {
  user: UserIdentity | null;
  account: AccountIdentityAccountFragment | null;
  accountId: string | null;
  ssoLoginUrl: string | null;
  isLoggedIn: boolean;
  isUser: boolean;
  isSudo: boolean;
  loading: boolean;
  error: Error | undefined;
  loggingIn: boolean;
  loginError: Error | undefined;
  refresh: () => void;
  login: (
    email: string,
    password: string,
    recaptchaToken: string,
  ) => Promise<LoginResult>;
  logout: (callbackUrl: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function clearUtmCookies() {
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'session_referrer',
    'ajs_user_id',
    'ajs_group_id',
    'ajs_anonymous_id',
    'ajs_user_traits',
    'ajs_group_properties',
    'ajs_user_properties',
    'ajs_event_properties',
    'ajs_event_properties',
    'AMP_unset_4affa0e463',
  ];

  // These cookies are set by Amplitude (and, in older sessions, the removed
  // Segment integration: `ajs_*`) on the parent (eTLD+1) domain so
  // they're shared across subdomains, e.g. `.apollographql.com`. Deleting with
  // only `path=/` (host-scoped) leaves the parent-domain cookie intact, so we
  // also delete with the apex domain. All Studio envs share the same apex.
  const host = window.location.hostname;
  const apex = host.split('.').slice(-2).join('.');

  for (const param of utmParams) {
    document.cookie = `${param}=; samesite=lax; path=/; max-age=0`;
    document.cookie = `${param}=; samesite=lax; path=/; max-age=0; domain=.${apex}`;
    document.cookie = `${param}=; samesite=lax; path=/; max-age=0; domain=${apex}`;
  }
}

/**
 * This context provides the global authentication state and methods for the app.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [{ loggingIn, logInSuccessful, error: loginError }, loginAction] =
    useLogin();
  const {
    me,
    accountId,
    myAccount,
    ssoLoginUrl,
    isLoggedIn,
    meLoading,
    error,
    refresh,
  } = useIdentity();
  const [isSudo] = useLocalStorage('sudo');

  const login = useCallback(
    async (email: string, password: string, recaptchaToken: string) => {
      const result = await loginAction(email, password, recaptchaToken);
      return result;
    },
    [loginAction],
  );

  const logout = async (callbackUrl: string) => {
    // Reset the analytics SDK first so its in-memory state can't re-persist
    // the user's identity into cookies/localStorage after we clear them below.
    resetAmplitude();

    clearUtmCookies();
    clearLocalStorage();
    Sentry.getCurrentScope().clear();
    datadogRum.clearUser();
    datadogRum.clearAccount();

    BackendRouter.go('Logout', { callbackUrl });
  };

  useEffect(() => {
    if (logInSuccessful) refresh();
  }, [logInSuccessful, refresh]);

  const value = useMemo(
    () => ({
      user: me,
      isUser: me?.__typename === 'User',
      account: myAccount,
      accountId,
      ssoLoginUrl,
      isLoggedIn,
      isSudo,
      loading: meLoading,
      error,
      loginError,
      loggingIn,
      login,
      logout,
      refresh,
    }),
    [
      me,
      myAccount,
      accountId,
      ssoLoginUrl,
      isLoggedIn,
      isSudo,
      meLoading,
      error,
      login,
      loggingIn,
      loginError,
      refresh,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth() {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');

  return context;
}
